import AuthorisedContainer from "components/AuthorisedContainer";
import DeleteButton from "components/DeleteButton";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import useModalRenderer from "hooks/useModalRenderer";
import { difference, filter, find, isUndefined, map } from "lodash";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { TierDto } from "types/api/tierBasedTagsDto";
import ModalUtil from "utils/modalUtil";
import Sorters from "utils/sorters";
import { getTagKey, getTagKeyMap } from "utils/tierUtil";
import { AddTagsToTierPopup } from "views/GlobalSettings/TierBasedTags";
import { AddTagsToTierPopup as AddTagsToTierPopupV2 } from "views/GlobalSettings/TierBasedTagsV2";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";

export type TagsType = { name: string; fieldValue: string; value: number };

interface UseTierBasedTagDataProps {
  value: TagsType[];
  onChange: (
    tags: TagsType[],
    { added, deleted }: { added: TagsType[]; deleted: TagsType[] },
  ) => void;
  tier: string;
  tierBasedTags?: TierDto[];
  allTags: TagsType[];
  editMode: boolean;
  actionTitle: string;
  permission: string;
  keyTitle: string;
  setEditingRiskAssessment?: Dispatch<SetStateAction<boolean>>;
  disableEditActions: boolean;
  isResizeable: boolean;
}

const useTierBasedTagData = ({
  allTags,
  tier,
  tierBasedTags,
  onChange,
  value,
  editMode,
  actionTitle,
  keyTitle,
  setEditingRiskAssessment,
  disableEditActions,
  isResizeable = false,
}: UseTierBasedTagDataProps) => {
  const featureFlags = useFeatureFlag();

  const tierObject = featureFlags.RISK_TIERS
    ? find(tierBasedTags, { id: tier })
    : undefined;
  const { context: modalContext, show: showModal } = useModalRenderer();
  const onDelete = useCallback(
    (note, deletedTag) => {
      const key = getTagKey(deletedTag);
      onChange(
        filter(value, (tagObj) => getTagKey(tagObj) !== key),
        { deleted: [deletedTag], added: [] },
      );
    },
    [onChange, value],
  );

  const saveTags = useCallback(
    (selectedTags) => {
      const tagKeyMap: { [key: string]: TagsType } = getTagKeyMap(allTags) as {
        [key: string]: TagsType;
      };
      const existingTags = map(value, getTagKey);
      const tags = map(selectedTags, getTagKey);
      const removedTags = difference(existingTags, tags);
      const addedTags = difference(tags, existingTags);
      onChange(
        tags.map((key) => tagKeyMap[key]),
        {
          added: addedTags.map((key) => tagKeyMap[key]),
          deleted: removedTags.map((key) => tagKeyMap[key]),
        },
      );
    },
    [allTags, onChange, value],
  );

  const addTagsClick = useCallback(() => {
    setEditingRiskAssessment?.(true);
    if (featureFlags.RISK_TIERS && tierObject) {
      showModal({
        content: (
          <AddTagsToTierPopupV2
            tier={tierObject}
            allTags={allTags}
            saveTags={async (selectedTags) => saveTags(selectedTags)}
          />
        ),
      });
    } else {
      ModalUtil.show({
        content: (
          //@ts-ignore
          <AddTagsToTierPopup
            //@ts-ignore
            tier={tier}
            tierBasedTags={tierBasedTags}
            allTags={allTags}
            saveTags={saveTags}
            tags={value}
          />
        ),
        afterClose: () => setEditingRiskAssessment?.(false),
      });
    }
  }, [
    allTags,
    tier,
    tierObject,
    saveTags,
    value,
    setEditingRiskAssessment,
    featureFlags.RISK_TIERS,
    showModal,
  ]);

  const columns = useMemo(() => {
    const _columns: ACTableColumnType<TagsType>[] = [
      {
        title: keyTitle ? keyTitle : "Key",
        dataIndex: "name",
        key: "name",
        width: 220,
        sorter: Sorters.STRING("name"),
      },
      {
        title: "Value",
        dataIndex: "fieldValue",
        key: "fieldValue",
        width: 180,
        sorter: Sorters.STRING("fieldValue"),
      },
      {
        title: "Weighting",
        dataIndex: "value",
        key: "value",
        width: 150,
      },
    ];
    if (editMode && !isResizeable) {
      _columns.push({
        title: !isUndefined(actionTitle) ? actionTitle : "Actions",
        align: "center",
        dataIndex: "actions",
        width: 60,
        fixed: "right",
        render: (value, currentRecord) => {
          return (
            <>
              <AuthorisedContainer
                permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
                tooltip="Delete Tag"
              >
                {/* @ts-ignore */}
                <DeleteButton
                  type="icon"
                  confirmText={"Delete"}
                  disabled={disableEditActions}
                  onDelete={onDelete}
                  data={currentRecord}
                  skipConfirm
                />
              </AuthorisedContainer>
            </>
          );
        },
      });
    }
    return _columns;
  }, [
    editMode,
    onDelete,
    actionTitle,
    keyTitle,
    disableEditActions,
    isResizeable,
  ]);

  const actionRenderer = useCallback(
    (record: TagsType) => {
      if (!editMode || !isResizeable) {
        return null;
      }
      return (
        <AuthorisedContainer
          permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
          tooltip="Delete Tag"
        >
          {/* @ts-ignore */}
          <DeleteButton
            type="icon"
            confirmText={"Delete"}
            disabled={disableEditActions}
            onDelete={onDelete}
            data={record}
            skipConfirm
          />
        </AuthorisedContainer>
      );
    },
    [editMode, isResizeable, disableEditActions, onDelete],
  );

  return {
    addTagsClick,
    columns,
    modalContext,
    actionRenderer: editMode && isResizeable ? actionRenderer : undefined,
  };
};

export default useTierBasedTagData;
